import React from 'react';
import { API_BASE } from '../../config';
import { DEVICE_TYPES, getDeviceDisplayName, APPLE_HEALTH_BRIDGE_DEVICES } from '../../constants/devices';
import DeviceDataViewerModal from './DeviceDataViewerModal';
import { isIOS, isAndroid, isMobile, getDeviceConnectionInstructions, getHHSAppStoreUrl } from '../../utils/platformDetect';

// Compatible wearable devices (informational grid)
const COMPATIBLE_WEARABLES = [
  { name: 'Apple Watch', icon: '⌚' },
  { name: 'Garmin', icon: '⌚' },
  { name: 'WHOOP', icon: '💪' },
  { name: 'Oura Ring', icon: '💍' },
  { name: 'Withings', icon: '⌚' },
  { name: 'Fitbit', icon: '⌚' },
  { name: 'Samsung Galaxy Watch', icon: '⌚' },
  { name: 'Samsung Galaxy Ring', icon: '💍' },
  { name: 'Polar', icon: '⌚' },
  { name: 'Suunto', icon: '⌚' },
  { name: 'Coros', icon: '⌚' },
  { name: 'Amazfit / Zepp', icon: '⌚' },
  { name: 'Eight Sleep', icon: '🛏️' },
];

function DevicesTab({ fetchWithAuth, setActiveTab }) {
  const [connectedDevices, setConnectedDevices] = React.useState([]);
  const [showDeviceDataViewer, setShowDeviceDataViewer] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState(null);
  const [deviceSubTab, setDeviceSubTab] = React.useState('wearable');
  const [showDisconnectModal, setShowDisconnectModal] = React.useState(null);
  const [disconnectOption, setDisconnectOption] = React.useState('source-only');

  const instructions = getDeviceConnectionInstructions();

  React.useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/devices/`);
      const devices = await response.json();
      setConnectedDevices(devices);
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const handleDisconnect = async (deviceType) => {
    try {
      // Honor the user's explicit choice. Default ('source-only') KEEPS synced data;
      // only 'source-and-data' purges it. Never let the backend assume delete.
      const deleteData = disconnectOption === 'source-and-data';
      const response = await fetchWithAuth(
        `${API_BASE}/api/devices/${deviceType.toLowerCase()}/disconnect?delete_data=${deleteData}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        loadDevices();
        setShowDisconnectModal(null);
      }
    } catch (error) {
      console.error('Failed to disconnect device:', error);
      alert('Failed to disconnect device');
    }
  };

  const handleViewDeviceData = (device) => {
    setSelectedDevice(device);
    setShowDeviceDataViewer(true);
  };

  // Filter devices
  const appleDevice = connectedDevices.find(d => d.device_type === 'apple' && d.is_connected);
  const otherDevices = connectedDevices.filter(d => d.device_type !== 'apple' && d.is_connected);

  // Disconnect confirmation modal
  const renderDisconnectModal = () => {
    if (!showDisconnectModal) return null;
    return (
      <div className="modal-overlay" onClick={() => setShowDisconnectModal(null)}>
        <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '440px'}}>
          <div className="modal-header">
            <h3 style={{color: 'white'}}>Disconnect {getDeviceDisplayName(showDisconnectModal)}</h3>
          </div>
          <div className="p-4">
            <p style={{marginBottom: '16px', fontSize: '14px'}}>How would you like to disconnect?</p>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer'}}>
              <input type="radio" name="disconnect" value="source-only"
                checked={disconnectOption === 'source-only'}
                onChange={() => setDisconnectOption('source-only')} />
              <span style={{fontSize: '14px'}}>Disconnect source only (keep synced data)</span>
            </label>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
              <input type="radio" name="disconnect" value="source-and-data"
                checked={disconnectOption === 'source-and-data'}
                onChange={() => setDisconnectOption('source-and-data')} />
              <span style={{fontSize: '14px', color: '#DC2626'}}>Disconnect and delete all associated data</span>
            </label>
            <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px'}}>
              <button className="btn btn-outline" onClick={() => setShowDisconnectModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{background: '#DC2626'}}
                onClick={() => handleDisconnect(showDisconnectModal)}>
                Disconnect
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Device Integration</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>← Dashboard</button>
        </div>

        <div className="page-nav-bar">
          <div className="tab-bar dmd-tabBar">
            <button
              className={`tab-item ${deviceSubTab === 'wearable' ? 'tab-item--active' : ''}`}
              onClick={() => setDeviceSubTab('wearable')}
            >
              Wearable Devices
            </button>
            <button
              className={`tab-item ${deviceSubTab === 'medical' ? 'tab-item--active' : ''}`}
              onClick={() => setDeviceSubTab('medical')}
            >
              Digital Medical Devices
            </button>
          </div>
        </div>
      </div>

      <div className="page-scroll-content">

      {/* ===== WEARABLE DEVICES TAB ===== */}
      {deviceSubTab === 'wearable' && (
        <div>
          {/* How to Connect */}
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="card-title">Connect Your Wearable Devices</h3>
            </div>
            <hr className="divider" />
            <div className="p-4">
              <div className="hrh-howTo" style={{margin: '0 0 16px'}}>
                <h4 className="hrh-howToTitle">How It Works — {isIOS ? 'via Apple Health' : isAndroid ? 'via Google Health Connect' : '3 Simple Steps'}</h4>
                <ol className="hrh-howToSteps">
                  {instructions.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
              <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                {(isIOS || !isMobile) && (
                  <a href="https://apps.apple.com/app/healist-health-sync/id6742478681"
                     target="_blank" rel="noopener noreferrer"
                     className="btn btn-primary" style={{textDecoration: 'none'}}>
                    Get HealistHealthSync (iOS)
                  </a>
                )}
                {(isAndroid || !isMobile) && (
                  <button className="btn btn-outline" disabled>
                    Google Health Connect (Coming Soon)
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Compatible Wearable Devices */}
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="card-title">Compatible Wearable Devices</h3>
            </div>
            <hr className="divider" />
            <div className="p-4">
              <p className="text-muted" style={{marginBottom: '12px'}}>
                These devices sync their data to Apple Health or Google Health Connect.
                Once connected via your mobile platform, data flows automatically to Healist.
              </p>
              <div className="dmd-bridgeGrid">
                {COMPATIBLE_WEARABLES.map((device, idx) => (
                  <div key={idx} className="dmd-bridgeCard">
                    <span className="dmd-bridgeIcon">{device.icon}</span>
                    <div className="dmd-bridgeInfo">
                      <span className="dmd-bridgeName">{device.name}</span>
                    </div>
                    {appleDevice ? (
                      <span className="dmd-bridgeStatus dmd-bridgeStatus--active">Active</span>
                    ) : (
                      <span className="dmd-bridgeStatus dmd-bridgeStatus--inactive">Connect via HHS</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Connected Devices Status */}
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="card-title">Your Connected Devices</h3>
            </div>
            <hr className="divider" />
            <div className="p-4">
              {!appleDevice && otherDevices.length === 0 ? (
                <p className="text-muted">No devices connected yet. Follow the instructions above to get started.</p>
              ) : (
                <>
                  {appleDevice && (
                    <div className="device-item">
                      <div className="device-info-row">
                        <div className="flex-1">
                          <span className="device-name">Apple Watch / Health (via HealistHealthSync)</span>
                        </div>
                        <div className="device-status">
                          <span className="device-status-connected">● Connected</span>
                          {appleDevice.last_sync && (
                            <span className="device-last-sync">Last sync: {new Date(appleDevice.last_sync).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="device-info-apple">
                        <div className="device-info-apple-content">
                          <span>ℹ️</span>
                          <div>
                            <span>Data syncs automatically every 2 hours via HealistHealthSync, or open HHS and tap Sync Now.</span>
                          </div>
                        </div>
                      </div>
                      <div className="device-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => handleViewDeviceData(appleDevice)}>
                          View Data
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => setShowDisconnectModal('apple')}>
                          Disconnect
                        </button>
                      </div>
                    </div>
                  )}
                  {otherDevices.filter(d => !['dexcom', 'withings'].includes(d.device_type)).map(device => (
                    <div key={device.id} className="device-item">
                      <div className="device-info-row">
                        <div className="flex-1">
                          <span className="device-name">{device.device_name || getDeviceDisplayName(device.device_type)}</span>
                          <span className="device-serial" style={{fontSize: '11px', color: '#6B7280'}}> (legacy direct connection)</span>
                        </div>
                        <div className="device-status">
                          <span className={device.is_connected ? 'device-status-connected' : 'device-status-disconnected'}>
                            {device.is_connected ? '● Connected' : '○ Disconnected'}
                          </span>
                        </div>
                      </div>
                      <div className="device-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => handleViewDeviceData(device)}>
                          View Data
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => setShowDisconnectModal(device.device_type)}>
                          Disconnect
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== DIGITAL MEDICAL DEVICES TAB ===== */}
      {deviceSubTab === 'medical' && (
        <div>
          {/* How to Connect Medical Devices */}
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="card-title">Digital Medical Devices</h3>
            </div>
            <hr className="divider" />
            <div className="p-4">
              <div className="hrh-howTo" style={{margin: '0 0 16px'}}>
                <h4 className="hrh-howToTitle">Same Simple Setup as Wearables</h4>
              <ol className="hrh-howToSteps">
                  <li>Open your medical device's companion app (Omron Connect, Renpho, Dexcom, etc.)</li>
                  <li>Enable sharing with {isIOS ? 'Apple Health' : isAndroid ? 'Google Health Connect' : 'Apple Health or Google Health Connect'}</li>
                  <li>HealistHealthSync syncs your data to Healist automatically.</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Compatible Medical Devices Grid */}
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="card-title">Compatible Medical Devices</h3>
            </div>
            <hr className="divider" />
            <div className="p-4">
              <div className="dmd-bridgeGrid">
                {APPLE_HEALTH_BRIDGE_DEVICES.map((bridgeDevice, idx) => (
                  <div key={idx} className="dmd-bridgeCard">
                    <span className="dmd-bridgeIcon">{bridgeDevice.icon}</span>
                    <div className="dmd-bridgeInfo">
                      <span className="dmd-bridgeName">{bridgeDevice.name}</span>
                      <span className="dmd-bridgeCategory">{bridgeDevice.category}</span>
                      <span className="dmd-bridgeDescription">{bridgeDevice.description}</span>
                    </div>
                    {appleDevice ? (
                      <span className="dmd-bridgeStatus dmd-bridgeStatus--active">Active</span>
                    ) : (
                      <span className="dmd-bridgeStatus dmd-bridgeStatus--inactive">Connect via HHS</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Modal */}
      {renderDisconnectModal()}

      {/* Device Data Viewer */}
      {showDeviceDataViewer && selectedDevice && (
        <DeviceDataViewerModal
          fetchWithAuth={fetchWithAuth}
          onClose={() => {
            setShowDeviceDataViewer(false);
            setSelectedDevice(null);
          }}
          device={selectedDevice}
        />
      )}
      </div>
    </div>
  );
}

export default DevicesTab;
